import {
  type ReportAccessConsumer,
  ReportAccessConsumerType,
  type ReportAccessProblem,
} from "shared/report-access/reportAccessTypes";

/** A labelled admin page to open from a report access indicator */
export type ReportAccessLink = {
  /** Button label */
  label: string;
  /** Admin path */
  href: string;
  /** Stable suffix for test ids */
  key: string;
};

type ConsumerLinksProps = {
  /** Current society */
  societyId: number;
  /** The consumer to link to */
  consumer: ReportAccessConsumer;
};

/** Where staff fix a consumer's side of the configuration */
export const getReportAccessConsumerLinks = ({
  societyId,
  consumer,
}: ConsumerLinksProps): ReportAccessLink[] => {
  const base = `/society/${societyId}`;
  switch (consumer.type) {
    case ReportAccessConsumerType.appRoute: {
      const links: ReportAccessLink[] = [
        {
          label: "Open route config",
          href: `${base}/settings/routes/${consumer.appRouteConfigId}`,
          key: `route-${consumer.appRouteConfigId}`,
        },
      ];
      if (consumer.directoryId !== undefined) {
        links.push({
          label: "Open directory",
          href: `${base}/settings/directories/${consumer.directoryId}/edit`,
          key: `directory-${consumer.directoryId}`,
        });
      }
      return links;
    }
    case ReportAccessConsumerType.directory:
      return [
        {
          label: "Open directory",
          href: `${base}/settings/directories/${consumer.id}/edit`,
          key: `directory-${consumer.id}`,
        },
      ];
    case ReportAccessConsumerType.form:
      return [
        {
          label: "Open form",
          href: `${base}/settings/form/${consumer.id}`,
          key: `form-${consumer.id}`,
        },
      ];
    default:
      return [];
  }
};

type ProblemLinksProps = {
  /** Current society */
  societyId: number;
  /** The problem to link from */
  problem: ReportAccessProblem;
};

/** The consumer's page, then the report and its permissions page */
export const getReportAccessProblemLinks = ({
  societyId,
  problem,
}: ProblemLinksProps): ReportAccessLink[] => {
  const links: ReportAccessLink[] = [];
  if (problem.consumer) {
    links.push(
      ...getReportAccessConsumerLinks({
        societyId,
        consumer: problem.consumer,
      }),
    );
  }
  if (problem.missingReport) {
    links.push({
      label: "Open route config",
      href: `/society/${societyId}/settings/routes/${problem.missingReport.appRouteConfigId}`,
      key: `route-${problem.missingReport.appRouteConfigId}`,
    });
  }
  if (problem.reportName !== undefined) {
    links.push(
      {
        label: "Open report",
        href: `/society/${societyId}/reports/${problem.customReportId}`,
        key: `report-${problem.customReportId}`,
      },
      {
        label: "Report permissions",
        href: `/society/${societyId}/reports/${problem.customReportId}/permissions`,
        key: `report-permissions-${problem.customReportId}`,
      },
    );
  }
  return links;
};
